import React, { useState } from "react";
import jsPDF from "jspdf";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import { Box, Modal, Button, FormGroup, FormControlLabel, Checkbox } from "@mui/material";
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend } from "chart.js";
import { translateText } from "../../../shared/utils/historyTranslation";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmt(v) {
  if (v == null || isNaN(v)) return "—";
  return Number(v).toFixed(2);
}

function avg(arr) {
  const valid = arr.filter((n) => Number.isFinite(n));
  if (valid.length === 0) return 0;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

const text = (translate, key, fallback) => translateText(translate, key, fallback);

export default function ReportGenerator({ open, onClose, centersData, translate }) {
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [includeAverages, setIncludeAverages] = useState(true);
  const [includeCharts, setIncludeCharts] = useState(true);

  const toggleCenter = (id) => {
    setSelectedCenters((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const generatePDF = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    let y = 20;

    pdf.setFontSize(18);
    pdf.text(text(translate, "dashboard_report_title", "Reporte de Monitoreo Nanoox"), pageWidth / 2, y, { align: "center" });
    y += 10;

    pdf.setFontSize(10);
    pdf.text(`${text(translate, "fecha", "Fecha")}: ${new Date().toLocaleString("es-CL")}`, 10, y);
    y += 10;

    // ============ PROMEDIOS ============
    if (includeAverages) {
      pdf.setFontSize(12);
      pdf.text(`${text(translate, "dashboard_general_averages", "Promedios Generales")}:`, 10, y);
      y += 8;

      centersData.forEach((center) => {
        const name = getCentroName(center);

        // Asegura que modulos, cans y bombas existan
        const modulos = Array.isArray(center?.modulos) ? center.modulos : [];
        const allBombas = modulos.flatMap((m) =>
          Array.isArray(m?.cans)
            ? m.cans.flatMap((c) => Array.isArray(c?.bombas) ? c.bombas : [])
            : []
        );

        if (allBombas.length === 0) {
          pdf.setFontSize(10);
          pdf.text(`${name}: ${text(translate, "dashboard_no_available_data", "sin datos disponibles")}`, 15, y);
          y += 6;
          return;
        }

        // Calcula promedios de forma segura
        const o2 = avg(allBombas.map((b) => safeNum(b?.O2)));
        const so2 = avg(allBombas.map((b) => safeNum(b?.SO2)));
        const temp = avg(allBombas.map((b) => safeNum(b?.Temp)));

        pdf.setFontSize(10);
        pdf.text(
          `${name}:  O₂ ${fmt(o2)} | SO₂ ${fmt(so2)} | Temp ${fmt(temp)}°C`,
          15,
          y
        );
        y += 6;
      });

      y += 6;
    }


    // ============ CENTROS ============
    for (const center of centersData) {
      if (!selectedCenters.includes(center.centro)) continue;
      const name = getCentroName(center);

      pdf.setFontSize(14);
      pdf.text(name, 10, y);
      y += 8;

      // === Tabla de datos ===
      pdf.setFontSize(10);
      pdf.text(`${text(translate, "dashboard_hourly_values", "Valores por hora")}:`, 10, y);
      y += 6;

      pdf.setFont("courier", "normal");
      pdf.text("Hora        |  O₂  |  SO₂  | Temp | Falla | Gen", 10, y);
      y += 5;

      // 🔹 Obtener bombas de forma segura
      const modulos = Array.isArray(center?.modulos) ? center.modulos : [];
      const bombas = modulos.flatMap((m) =>
        Array.isArray(m?.cans) ? m.cans.flatMap((c) => c?.bombas || []) : []
      );

      // Si no hay bombas, saltar
      if (!bombas.length) {
        pdf.text(text(translate, "dashboard_no_pump_data", "No hay datos de bombas disponibles."), 10, y);
        y += 10;
        continue;
      }

      bombas.forEach((b) => {
        const lm = b?.latest_measurements;
        if (!lm || !Array.isArray(lm.TS)) return;

        lm.TS.slice(-6).forEach((ts, i) => {
          const o2 = safeNum(lm?.O2?.[i]);
          const so2 = safeNum(lm?.SO2?.[i]);
          const temp = safeNum(lm?.Temp?.[i]);
          const row = `${new Date(ts).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })} | ${fmt(o2)} | ${fmt(so2)} | ${fmt(temp)} | ${b?.InStop ? "Sí" : "No"} | ${b?.Device_Modbus_Status ? "OK" : "Error"}`;
          pdf.text(row, 10, y);
          y += 5;
          if (y > 270) {
            pdf.addPage();
            y = 20;
          }
        });
      });

      // === Umbrales ===
      pdf.setFont("helvetica", "normal");
      const umbrales = center?.umbrales;
      pdf.text(
        `Umbral ON: ${umbrales?.setpoin_on ?? "—"} | OFF: ${umbrales?.setpoin_off ?? "—"}`,
        10,
        y
      );
      y += 8;

      // === Gráfico regenerado ===
      if (includeCharts && bombas[0]?.latest_measurements?.TS?.length) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const lm = bombas[0].latest_measurements;
        const data = {
          labels: lm.TS.slice(-10).map((t) =>
            new Date(t).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })
          ),
          datasets: [
            {
              label: "O₂ (mg/L)",
              data: lm.O2?.slice(-10) || [],
              borderColor: "#00bcd4",
              fill: false,
            },
            {
              label: "Temp (°C)",
              data: lm.Temp?.slice(-10) || [],
              borderColor: "#e84e0e",
              fill: false,
            },
          ],
        };

        await new Promise((resolve) => {
          new ChartJS(ctx, {
            type: "line",
            data,
            options: { responsive: false, animation: false, scales: { y: { beginAtZero: true } } },
            plugins: [
              {
                id: "done",
                afterRender: () => {
                  const img = canvas.toDataURL("image/png");
                  pdf.addImage(img, "PNG", 10, y, 180, 60);
                  y += 70;
                  resolve();
                },
              },
            ],
          });
        });
      }
    }

    pdf.save("Reporte_Nanoox.pdf");
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        bgcolor: "#1e1e1e", color: "#fff", boxShadow: 24, p: 3, borderRadius: 2,
        width: "40vw", maxHeight: "70vh", overflowY: "auto",
      }}>
        <h2>{text(translate, "dashboard_configure_report", "Configurar Reporte")}</h2>
        <FormGroup>
          {centersData.map((c) => (
            <FormControlLabel
              key={c.centro}
              control={<Checkbox checked={selectedCenters.includes(c.centro)} onChange={() => toggleCenter(c.centro)} />}
              label={getCentroName(c)}
            />
          ))}
        </FormGroup>

        <FormControlLabel
          control={<Checkbox checked={includeAverages} onChange={() => setIncludeAverages(!includeAverages)} />}
          label={text(translate, "dashboard_include_global_averages", "Incluir promedios globales")}
        />

        <FormControlLabel
          control={<Checkbox checked={includeCharts} onChange={() => setIncludeCharts(!includeCharts)} />}
          label={text(translate, "dashboard_include_center_charts", "Incluir graficos por centro")}
        />

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" color="secondary" onClick={onClose}>{text(translate, "cancel", "Cancelar")}</Button>
          <Button variant="contained" color="primary" onClick={generatePDF}>{text(translate, "dashboard_generate_pdf", "Generar PDF")}</Button>
        </Box>
      </Box>
    </Modal>
  );
}

// Utilidades
function getCentroName(center) {
  const fullId = center?.centro || center?.modulos?.[0]?.cans?.[0]?.centro_id;
  if (!fullId) return "—";
  const parts = fullId.split("-");
  return parts[parts.length - 1].trim();
}
